import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Card, PageHeader, Badge, Skeleton, StatCard } from '../components'
import { auditApi, teamsApi } from '../api'
import type { AuditLog, AuditSummaryResponse } from '../api/audit'
import type { TeamMember } from '../api/teams'
import {
  History,
  User,
  Clock,
  Filter,
  ChevronDown,
  ChevronUp,
  Activity,
  Users,
  TrendingUp,
} from 'lucide-react'

// Action type labels
const ACTION_LABELS: Record<string, string> = {
  'app.create': 'Created app',
  'app.update': 'Updated app',
  'app.delete': 'Deleted app',
  'error.resolve': 'Resolved error',
  'error.unresolve': 'Unresolved error',
  'error.ignore': 'Ignored error',
  'error.mute': 'Muted error',
  'error.unmute': 'Unmuted error',
  'error.assign': 'Assigned error',
  'error.unassign': 'Unassigned error',
  'error.bulk_update': 'Bulk updated errors',
  'alert.create': 'Created alert',
  'alert.update': 'Updated alert',
  'alert.delete': 'Deleted alert',
  'cron.create': 'Created cron monitor',
  'cron.update': 'Updated cron monitor',
  'cron.delete': 'Deleted cron monitor',
  'cron.pause': 'Paused cron monitor',
  'cron.resume': 'Resumed cron monitor',
  'filter.create': 'Created filter',
  'filter.update': 'Updated filter',
  'filter.delete': 'Deleted filter',
  'source_map.upload': 'Uploaded source map',
  'source_map.delete': 'Deleted source map',
  'source_map.delete_release': 'Deleted source maps for release',
  'status_page.create': 'Created status page',
  'status_page.update': 'Updated status page',
  'status_page.delete': 'Deleted status page',
  'incident.create': 'Created incident',
  'incident.update': 'Updated incident',
  'incident.resolve': 'Resolved incident',
  'member.invite': 'Invited member',
  'member.remove': 'Removed member',
  'member.role_change': 'Changed member role',
  'api_key.create': 'Created API key',
  'api_key.revoke': 'Revoked API key',
}

// Action categories for filtering
const ACTION_CATEGORIES = [
  { label: 'All', value: '' },
  { label: 'Apps', value: 'app' },
  { label: 'Errors', value: 'error' },
  { label: 'Alerts', value: 'alert' },
  { label: 'Cron Jobs', value: 'cron' },
  { label: 'Source Maps', value: 'source_map' },
  { label: 'Status Pages', value: 'status_page' },
  { label: 'Incidents', value: 'incident' },
  { label: 'Members', value: 'member' },
  { label: 'API Keys', value: 'api_key' },
]

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  })
}

function AuditLogRow({
  log,
  members,
  expanded,
  onToggle,
}: {
  log: AuditLog
  members: TeamMember[]
  expanded: boolean
  onToggle: () => void
}) {
  const member = members.find(m => m.user_id === log.user_id)
  const hasChanges = log.changes && Object.keys(log.changes).length > 0

  return (
    <div className="border-b border-slate-100 last:border-0">
      <div
        className={`flex items-center justify-between p-4 hover:bg-slate-50 transition-colors ${hasChanges ? 'cursor-pointer' : ''}`}
        onClick={hasChanges ? onToggle : undefined}
      >
        <div className="flex items-center gap-4 min-w-0">
          <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
            <User className="w-4 h-4 text-accent-600" />
          </div>
          <div className="min-w-0">
            <p className="text-sm text-slate-900">
              <span className="font-medium">{member?.name || 'Unknown user'}</span>{' '}
              <span className="text-slate-600">{ACTION_LABELS[log.action] || log.action}</span>
              {log.resource_name && (
                <>
                  {' '}
                  <span className="font-medium text-slate-900">{log.resource_name}</span>
                </>
              )}
            </p>
            <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
              <Clock className="w-3 h-3" />
              <span>{formatDate(log.created_at)}</span>
              {log.ip_address && (
                <>
                  <span className="text-slate-300">•</span>
                  <span>{log.ip_address}</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="gray" className="text-xs">
            {log.resource_type}
          </Badge>
          {hasChanges && (
            <button className="p-1 text-slate-400 hover:text-slate-600">
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          )}
        </div>
      </div>

      {expanded && hasChanges && (
        <div className="px-4 pb-4 ml-12">
          <div className="bg-slate-50 rounded-lg p-3 text-sm">
            <p className="text-xs font-medium text-slate-500 mb-2">Changes</p>
            <div className="space-y-1">
              {Object.entries(log.changes!).map(([field, change]) => (
                <div key={field} className="flex items-start gap-2">
                  <span className="font-medium text-slate-700">{field}:</span>
                  <span className="text-red-600 line-through">{JSON.stringify(change.old)}</span>
                  <span className="text-slate-400">→</span>
                  <span className="text-green-600">{JSON.stringify(change.new)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default function AuditLogPage() {
  const { teamId } = useParams<{ teamId: string }>()
  const [logs, setLogs] = useState<AuditLog[]>([])
  const [summary, setSummary] = useState<AuditSummaryResponse | null>(null)
  const [members, setMembers] = useState<TeamMember[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [actionFilter, setActionFilter] = useState('')
  const [userFilter, setUserFilter] = useState('')
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const limit = 50

  useEffect(() => {
    const loadData = async () => {
      try {
        const [logsRes, summaryRes, membersRes] = await Promise.all([
          auditApi.list({ limit, offset: 0 }),
          auditApi.getSummary(30),
          teamId ? teamsApi.getMembers(teamId) : Promise.resolve({ members: [] }),
        ])
        setLogs(logsRes.logs)
        setTotal(logsRes.total)
        setSummary(summaryRes)
        setMembers(membersRes.members)
      } catch (error) {
        console.error('Failed to load audit logs:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [teamId])

  // Reload logs when filters change
  useEffect(() => {
    const loadLogs = async () => {
      try {
        const filters: Record<string, string | number> = { limit, offset }
        if (actionFilter) {
          // Filter by action prefix
          filters.action = actionFilter
        }
        if (userFilter) {
          filters.user_id = userFilter
        }

        const logsRes = await auditApi.list(filters)
        setLogs(logsRes.logs)
        setTotal(logsRes.total)
      } catch (error) {
        console.error('Failed to load audit logs:', error)
      }
    }

    if (!isLoading) {
      loadLogs()
    }
  }, [actionFilter, userFilter, offset, isLoading])

  const loadMore = () => {
    setOffset(prev => prev + limit)
  }

  if (isLoading) {
    return (
      <>
        <PageHeader title="Audit Log" subtitle="Track team activity and changes" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-24 rounded-xl" />
        </div>
        <Card className="p-0">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="p-4 border-b border-slate-100">
              <Skeleton className="h-12" />
            </div>
          ))}
        </Card>
      </>
    )
  }

  return (
    <>
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <History className="w-6 h-6 text-accent-600" />
            Audit Log
          </span>
        }
        subtitle="Track team activity and changes"
      />

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <StatCard
            icon={Activity}
            title="Total Actions"
            subtitle="Last 30 days"
            value={summary.total_actions.toLocaleString()}
          />
          <StatCard
            icon={Users}
            title="Active Users"
            subtitle="Last 30 days"
            value={summary.active_users.toString()}
          />
          <StatCard
            icon={TrendingUp}
            title="Most Common"
            subtitle="Action type"
            value={
              Object.entries(summary.by_action)
                .sort((a, b) => b[1] - a[1])[0]?.[0]
                ?.split('.')[1] || 'N/A'
            }
          />
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <select
            value={actionFilter}
            onChange={e => {
              setActionFilter(e.target.value)
              setOffset(0)
            }}
            className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-accent-500"
          >
            {ACTION_CATEGORIES.map(cat => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>
        <select
          value={userFilter}
          onChange={e => {
            setUserFilter(e.target.value)
            setOffset(0)
          }}
          className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-accent-500"
        >
          <option value="">All Users</option>
          {members.map(member => (
            <option key={member.user_id} value={member.user_id}>
              {member.name}
            </option>
          ))}
        </select>
        {(actionFilter || userFilter) && (
          <button
            onClick={() => {
              setActionFilter('')
              setUserFilter('')
              setOffset(0)
            }}
            className="text-sm text-accent-600 hover:text-accent-700"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Log list */}
      <Card className="p-0 overflow-hidden">
        {logs.length === 0 ? (
          <div className="p-8 text-center">
            <History className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-slate-900 mb-2">No activity yet</h4>
            <p className="text-slate-500">Team actions will appear here as they happen.</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-slate-100">
              {logs.map(log => (
                <AuditLogRow
                  key={log.id}
                  log={log}
                  members={members}
                  expanded={expandedId === log.id}
                  onToggle={() => setExpandedId(expandedId === log.id ? null : log.id)}
                />
              ))}
            </div>

            {logs.length < total && (
              <div className="p-4 text-center border-t border-slate-100">
                <button
                  onClick={loadMore}
                  className="text-sm text-accent-600 hover:text-accent-700 font-medium"
                >
                  Load more ({total - logs.length} remaining)
                </button>
              </div>
            )}
          </>
        )}
      </Card>
    </>
  )
}
